import { getAccessToken } from '@/lib/tokenStorage';

import {
  AddShipmentInput,
  EditShipmentInput,
  ShipmentApiResponse,
  ShipmentPayload,
} from './types';

async function addShipment(input: AddShipmentInput): Promise<ShipmentPayload> {
  const url = `${import.meta.env.VITE_API_BASE_URL}/api/v1/shipping`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function getShipments(
  searchParams: URLSearchParams = new URLSearchParams(),
): Promise<ShipmentApiResponse> {
  const queryString = searchParams.toString();

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  const url = `${API_BASE_URL}/api/v2/shipping/shipments${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function editShipments(
  inputs: EditShipmentInput[],
): Promise<ShipmentPayload[]> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not defined');
  }
  const url = `${API_BASE_URL}/api/v1/shipping`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  try {
    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(inputs),
    });

    if (!response.ok && response.status !== 400) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error status: ${response.status}, message: ${errorText}`,
      );
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error updating shipments:', error);
    throw error;
  }
}

export const shipmentApi = {
  editShipments,
  getShipments,
  addShipment,
};
